import { db } from '../config/firebase';
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';

export interface SimplifiedBalanceData {
  totalBalance: number; // Base balance (starts at $1000, modified by deposits/withdrawals and P&L)
  usedMargin: number; // Total margin used across all open positions
  availableBalance: number; // Calculated as totalBalance - usedMargin
  equity: number; // Total Balance + Realized P&L + Unrealized P&L
  realizedPnL: number; // Total realized P&L from all closed positions
  currency: string;
  lastUpdated: string;
}

export interface PositionMarginData {
  positionId: string;
  symbol: string;
  marginAmount: number;
  openedAt: string;
}

export interface BalanceTransaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'position_open' | 'position_close' | 'profit' | 'loss';
  amount: number;
  description: string;
  timestamp: string;
  positionId?: string;
}

export class SimplifiedBalanceService {
  private static readonly COLLECTION_NAME = 'userDocuments';
  private static readonly DEMO_INITIAL_BALANCE = 1000; // $1000 initial balance for demo
  private static readonly LIVE_INITIAL_BALANCE = 0; // $0 initial balance for live
  
  // Event emitter for balance updates
  private static balanceUpdateCallbacks: Set<() => void> = new Set();
  
  /**
   * Subscribe to balance updates
   */
  static onBalanceUpdate(callback: () => void): () => void {
    this.balanceUpdateCallbacks.add(callback);
    return () => this.balanceUpdateCallbacks.delete(callback);
  }
  
  /**
   * Trigger balance update callbacks
   */
  private static triggerBalanceUpdate(): void {
    this.balanceUpdateCallbacks.forEach(callback => callback());
  }
  
  /**
   * Map account type from UI ('live') to Firestore ('real')
   */
  private static mapAccountType(accountType: 'demo' | 'live'): 'demo' | 'real' {
    return accountType === 'live' ? 'real' : 'demo';
  }

  /**
   * Calculate equity with unrealized P&L
   * @param totalBalance - Current total balance
   * @param unrealizedPnL - Unrealized P&L from open positions
   * @returns Equity value
   */
  static calculateEquity(totalBalance: number, unrealizedPnL: number): number {
    return totalBalance + unrealizedPnL;
  }

  /**
   * Restate the account against the positions that are actually open.
   *
   * Used margin is set here rather than adjusted. `onPositionOpen` and `onPositionClose` add
   * and subtract it, and any increment that is missed or applied twice stays in the stored
   * figure forever — which is how an account with nothing open ends up still showing hundreds
   * of dollars locked. Recomputing from the live positions on a schedule means that arithmetic
   * only ever has to be right until the next sync, and a flat account settles back to zero.
   *
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'live')
   * @param usedMargin - Margin of every position currently open, summed
   * @param unrealizedPnL - Unrealized P&L of those same positions
   * @returns Updated balance data, or null when nothing needed changing
   */
  static async syncPositionState(
    userId: string,
    accountType: 'demo' | 'live',
    usedMargin: number,
    unrealizedPnL: number
  ): Promise<SimplifiedBalanceData | null> {
    try {
      const currentBalance = await this.getBalanceFromFirestore(userId, accountType);
      if (!currentBalance) {
        throw new Error('Could not retrieve current balance');
      }

      const equity = this.calculateEquity(currentBalance.totalBalance, unrealizedPnL);

      // Sub-cent differences are rounding, not news, and writing them would keep the
      // document churning while a position is open.
      const settled =
        Math.abs(currentBalance.usedMargin - usedMargin) < 0.005 &&
        Math.abs(currentBalance.equity - equity) < 0.005;

      if (settled) return currentBalance;

      const updatedBalance: SimplifiedBalanceData = {
        ...currentBalance,
        usedMargin,
        availableBalance: currentBalance.totalBalance - usedMargin,
        equity,
        lastUpdated: new Date().toISOString()
      };

      await this.updateBalanceInFirestore(userId, this.mapAccountType(accountType), updatedBalance);
      
      return updatedBalance;
    } catch (error) {
      console.error(`❌ Error syncing position state for ${accountType} account:`, error);
      throw error;
    }
  }
  
  /**
   * Reset Live account balance to $0 (for existing users who had incorrect $1000)
   * @param userId - User ID
   * @returns Reset balance data
   */
  static async resetLiveAccountBalance(userId: string): Promise<SimplifiedBalanceData> {
    const resetBalance: SimplifiedBalanceData = {
      totalBalance: 0,
      usedMargin: 0,
      availableBalance: 0,
      equity: 0,
      realizedPnL: 0,
      currency: 'USD',
      lastUpdated: new Date().toISOString()
    };

    await this.updateBalanceInFirestore(userId, 'real', resetBalance);
    
    // console.log('✅ Live account balance reset to $0.00');
    return resetBalance;
  }

  /**
   * Reset the demo account to a starting balance, keeping any margin currently locked by
   * open positions so the account cannot appear to have money that is already committed.
   *
   * The refill button used to write `accountSummary` on its own. Every balance decision is
   * made from `simplifiedBalance`, which was left untouched, so the topped-up figure showed
   * in the header but the next trade was sized against the old balance — and the first write
   * from a trade replaced the header figure with the old one again.
   */
  static async resetDemoAccountBalance(
    userId: string,
    amount: number = this.DEMO_INITIAL_BALANCE
  ): Promise<SimplifiedBalanceData> {
    const currentBalance = await this.getBalanceFromFirestore(userId, 'demo');
    const usedMargin = currentBalance?.usedMargin ?? 0;

    const resetBalance: SimplifiedBalanceData = {
      totalBalance: amount,
      usedMargin,
      availableBalance: amount - usedMargin,
      equity: amount,
      realizedPnL: 0,
      currency: currentBalance?.currency ?? 'USD',
      lastUpdated: new Date().toISOString()
    };

    await this.updateBalanceInFirestore(userId, 'demo', resetBalance);

    return resetBalance;
  }

  /**
   * Initialize balance for a new account
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @returns Initialized balance data
   */
  static async initializeBalance(
    userId: string, 
    accountType: 'demo' | 'live'
  ): Promise<SimplifiedBalanceData> {
    const initialAmount = accountType === 'demo' ? this.DEMO_INITIAL_BALANCE : this.LIVE_INITIAL_BALANCE;
    
    const initialBalance: SimplifiedBalanceData = {
      totalBalance: initialAmount,
      usedMargin: 0,
      availableBalance: initialAmount,
      equity: initialAmount, // Initially equity equals total balance (no unrealized P&L)
      realizedPnL: 0, // No realized P&L initially
      currency: 'USD',
      lastUpdated: new Date().toISOString()
    };

    await this.updateBalanceInFirestore(userId, this.mapAccountType(accountType), initialBalance);
    
    // Log initial balance transaction only if there's an initial amount
    if (initialAmount > 0) {
      await this.logTransaction(userId, accountType, {
        type: 'deposit',
        amount: initialAmount,
        description: 'Initial account balance',
        timestamp: new Date().toISOString()
      });
    }

    return initialBalance;
  }

  /**
   * Get current balance data from Firestore
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @returns Current balance data or null if not found
   */
  static async getBalanceFromFirestore(
    userId: string, 
    accountType: 'demo' | 'live'
  ): Promise<SimplifiedBalanceData | null> {
    try {
      const userDocRef = doc(db, this.COLLECTION_NAME, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();
      const accountData = userData.accounts?.[this.mapAccountType(accountType)];
      
      // Try to get balance from simplifiedBalance first, then fallback to accountSummary
      let balanceData = accountData?.simplifiedBalance;
      
      if (!balanceData && accountData?.accountSummary) {
        // Fallback to accountSummary if simplifiedBalance doesn't exist
        const accountSummary = accountData.accountSummary;
        balanceData = {
        totalBalance: accountSummary.balance?.totalBalance || (accountType === 'demo' ? this.DEMO_INITIAL_BALANCE : this.LIVE_INITIAL_BALANCE),
        usedMargin: accountSummary.margin?.usedMargin || 0,
        availableBalance: accountSummary.balance?.availableBalance || (accountType === 'demo' ? this.DEMO_INITIAL_BALANCE : this.LIVE_INITIAL_BALANCE),
        equity: accountSummary.equity?.totalEquity || (accountType === 'demo' ? this.DEMO_INITIAL_BALANCE : this.LIVE_INITIAL_BALANCE),
          realizedPnL: accountSummary.pnl?.realizedPnL || 0,
          currency: accountSummary.balance?.currency || 'USD',
          lastUpdated: new Date().toISOString()
        };
      }

      if (!balanceData) {
        // Initialize balance if neither simplifiedBalance nor accountSummary exists
        return await this.initializeBalance(userId, accountType);
      }

      const result = {
        totalBalance: balanceData.totalBalance || (accountType === 'demo' ? this.DEMO_INITIAL_BALANCE : this.LIVE_INITIAL_BALANCE),
        usedMargin: balanceData.usedMargin || 0,
        availableBalance: balanceData.availableBalance || (accountType === 'demo' ? this.DEMO_INITIAL_BALANCE : this.LIVE_INITIAL_BALANCE),
        equity: balanceData.equity || (accountType === 'demo' ? this.DEMO_INITIAL_BALANCE : this.LIVE_INITIAL_BALANCE),
        realizedPnL: balanceData.realizedPnL || 0,
        currency: balanceData.currency || 'USD',
        lastUpdated: balanceData.lastUpdated || new Date().toISOString()
      };
      
      return result;
    } catch (error) {
      console.error(`❌ Error getting balance from Firestore for ${accountType} account:`, error);
      return null;
    }
  }

  /**
   * Update balance when a position is opened
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param marginRequired - Margin required for the new position
   * @param positionId - Position ID
   * @param symbol - Trading symbol
   * @returns Updated balance data
   */
  static async onPositionOpen(
    userId: string,
    accountType: 'demo' | 'live',
    marginRequired: number,
    positionId: string,
    symbol: string
  ): Promise<SimplifiedBalanceData> {
    try {
      const currentBalance = await this.getBalanceFromFirestore(userId, accountType);
      if (!currentBalance) {
        throw new Error('Could not retrieve current balance');
      }

      // Check if sufficient available balance
      if (currentBalance.availableBalance < marginRequired) {
        throw new Error(`Insufficient balance. Required: $${marginRequired.toFixed(2)}, Available: $${currentBalance.availableBalance.toFixed(2)}`);
      }

      // Update balance: deduct margin from available balance
      const newUsedMargin = currentBalance.usedMargin + marginRequired; // Add to used margin
      const newAvailableBalance = currentBalance.totalBalance - newUsedMargin; // Available = Total - Used Margin
      
      
      const updatedBalance: SimplifiedBalanceData = {
        totalBalance: currentBalance.totalBalance, // Total balance stays the same
        usedMargin: newUsedMargin,
        availableBalance: newAvailableBalance,
        equity: currentBalance.equity, // Equity stays the same initially (no unrealized P&L yet)
        realizedPnL: currentBalance.realizedPnL, // Realized P&L stays the same
        currency: currentBalance.currency,
        lastUpdated: new Date().toISOString()
      };

      await this.updateBalanceInFirestore(userId, this.mapAccountType(accountType), updatedBalance);
      
      // Log position open transaction
      await this.logTransaction(userId, accountType, {
        type: 'position_open',
        amount: marginRequired,
        description: `Margin locked for ${symbol} position`,
        timestamp: new Date().toISOString(),
        positionId
      });

      return updatedBalance;
    } catch (error) {
      console.error(`❌ Error opening position for ${accountType} account:`, error);
      throw error;
    }
  }

  /**
   * Update balance when a position is closed
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param marginReleased - Margin released from closed position
   * @param pnl - Profit/Loss from the position
   * @param positionId - Position ID
   * @param symbol - Trading symbol
   * @param remainingUnrealizedPnL - Unrealized P&L of the positions still open afterwards
   * @returns Updated balance data
   */
  static async onPositionClose(
    userId: string,
    accountType: 'demo' | 'live',
    marginReleased: number,
    pnl: number,
    positionId: string,
    symbol: string,
    remainingUnrealizedPnL: number = 0
  ): Promise<SimplifiedBalanceData> {
    try {
      const currentBalance = await this.getBalanceFromFirestore(userId, accountType);
      if (!currentBalance) {
        throw new Error('Could not retrieve current balance');
      }

      // Update balance: release margin and add/subtract P&L
      // Correct formula: New Total Balance = Previous Total Balance + P&L (margin is just released, not added to balance)
      const newTotalBalance = currentBalance.totalBalance + pnl;
      const newUsedMargin = Math.max(0, currentBalance.usedMargin - marginReleased); // Release margin
      const newAvailableBalance = newTotalBalance - newUsedMargin; // Available = Total - Used Margin
      
      
      const updatedBalance: SimplifiedBalanceData = {
        totalBalance: newTotalBalance,
        usedMargin: newUsedMargin,
        availableBalance: newAvailableBalance,
        // This position's P&L is realised into the balance, but any position still open keeps
        // contributing its unrealized P&L. Flattening equity to the balance here made every
        // other open trade's profit vanish from the header until the next price tick.
        equity: newTotalBalance + remainingUnrealizedPnL,
        realizedPnL: currentBalance.realizedPnL + pnl, // Add P&L to total realized P&L
        currency: currentBalance.currency,
        lastUpdated: new Date().toISOString()
      };

      await this.updateBalanceInFirestore(userId, this.mapAccountType(accountType), updatedBalance);
      
      // Log position close transaction
      await this.logTransaction(userId, accountType, {
        type: pnl >= 0 ? 'profit' : 'loss',
        amount: Math.abs(pnl),
        description: `${symbol} position closed - ${pnl >= 0 ? 'Profit' : 'Loss'}: $${Math.abs(pnl).toFixed(2)}, Margin released: $${marginReleased.toFixed(2)}`,
        timestamp: new Date().toISOString(),
        positionId
      });

      return updatedBalance;
    } catch (error) {
      console.error(`❌ Error closing position for ${accountType} account:`, error);
      throw error;
    }
  }

  /**
   * Process deposit
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param amount - Deposit amount
   * @param description - Transaction description
   * @returns Updated balance data
   */
  static async processDeposit(
    userId: string,
    accountType: 'demo' | 'live',
    amount: number,
    description: string
  ): Promise<SimplifiedBalanceData> {
    try {
      const currentBalance = await this.getBalanceFromFirestore(userId, accountType);
      if (!currentBalance) {
        throw new Error('Could not retrieve current balance');
      }

      // Add deposit to total balance
      const newTotalBalance = currentBalance.totalBalance + amount;
      const newAvailableBalance = newTotalBalance - currentBalance.usedMargin; // Available = Total - Used Margin
      
      const updatedBalance: SimplifiedBalanceData = {
        totalBalance: newTotalBalance,
        usedMargin: currentBalance.usedMargin, // Margin stays the same
        availableBalance: newAvailableBalance,
        equity: newTotalBalance, // Equity = Total Balance (no unrealized P&L for deposit)
        realizedPnL: currentBalance.realizedPnL, // Realized P&L stays the same
        currency: currentBalance.currency,
        lastUpdated: new Date().toISOString()
      };

      await this.updateBalanceInFirestore(userId, this.mapAccountType(accountType), updatedBalance);
      
      // Log deposit transaction
      await this.logTransaction(userId, accountType, {
        type: 'deposit',
        amount,
        description,
        timestamp: new Date().toISOString()
      });

      return updatedBalance;
    } catch (error) {
      console.error(`❌ Error processing deposit for ${accountType} account:`, error);
      throw error;
    }
  }

  /**
   * Process withdrawal
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param amount - Withdrawal amount
   * @param description - Transaction description
   * @returns Updated balance data
   */
  static async processWithdrawal(
    userId: string,
    accountType: 'demo' | 'live',
    amount: number,
    description: string
  ): Promise<SimplifiedBalanceData> {
    try {
      const currentBalance = await this.getBalanceFromFirestore(userId, accountType);
      if (!currentBalance) {
        throw new Error('Could not retrieve current balance');
      }

      // Check if sufficient available balance
      if (currentBalance.availableBalance < amount) {
        throw new Error(`Insufficient available balance. Required: $${amount.toFixed(2)}, Available: $${currentBalance.availableBalance.toFixed(2)}`);
      }

      // Subtract withdrawal from total balance
      const newTotalBalance = currentBalance.totalBalance - amount;
      const newAvailableBalance = newTotalBalance - currentBalance.usedMargin; // Available = Total - Used Margin
      
      const updatedBalance: SimplifiedBalanceData = {
        totalBalance: newTotalBalance,
        usedMargin: currentBalance.usedMargin, // Margin stays the same
        availableBalance: newAvailableBalance,
        equity: newTotalBalance, // Equity = Total Balance (no unrealized P&L for withdrawal)
        realizedPnL: currentBalance.realizedPnL, // Realized P&L stays the same
        currency: currentBalance.currency,
        lastUpdated: new Date().toISOString()
      };

      await this.updateBalanceInFirestore(userId, this.mapAccountType(accountType), updatedBalance);
      
      // Log withdrawal transaction
      await this.logTransaction(userId, accountType, {
        type: 'withdrawal',
        amount,
        description,
        timestamp: new Date().toISOString()
      });

      return updatedBalance;
    } catch (error) {
      console.error(`❌ Error processing withdrawal for ${accountType} account:`, error);
      throw error;
    }
  }

  /**
   * Update balance data in Firestore
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param balanceData - Balance data to store
   */
  private static async updateBalanceInFirestore(
    userId: string,
    accountType: 'demo' | 'real',
    balanceData: SimplifiedBalanceData
  ): Promise<void> {
    try {
      const userDocRef = doc(db, this.COLLECTION_NAME, userId);

      // Everything in accountSummary is derived here from the figures actually stored, so
      // the numbers the header reads can never drift from `simplifiedBalance`.
      //
      // `equity` already carries the open positions' unrealized P&L, which is what makes it
      // differ from `totalBalance`, so the unrealized figure can be recovered from the two.
      const unrealizedPnL = balanceData.equity - balanceData.totalBalance;

      // Free margin and margin level are measured against equity, not balance: money still
      // at risk in an open position counts against what is left to trade with. These were
      // previously mirrored from `availableBalance` and left unwritten respectively, so the
      // header showed free margin that ignored open P&L and a margin level that never moved.
      const freeMargin = balanceData.equity - balanceData.usedMargin;
      const marginLevel = balanceData.usedMargin > 0
        ? (balanceData.equity / balanceData.usedMargin) * 100
        : 0;

      await updateDoc(userDocRef, {
        [`accounts.${accountType}.simplifiedBalance`]: balanceData,
        [`accounts.${accountType}.accountSummary.balance.totalBalance`]: balanceData.totalBalance,
        [`accounts.${accountType}.accountSummary.balance.availableBalance`]: balanceData.availableBalance,
        [`accounts.${accountType}.accountSummary.balance.lockedBalance`]: balanceData.usedMargin,
        [`accounts.${accountType}.accountSummary.margin.freeMargin`]: freeMargin,
        [`accounts.${accountType}.accountSummary.margin.usedMargin`]: balanceData.usedMargin,
        [`accounts.${accountType}.accountSummary.margin.marginLevel`]: marginLevel,
        [`accounts.${accountType}.accountSummary.equity.totalEquity`]: balanceData.equity,
        [`accounts.${accountType}.accountSummary.equity.freeEquity`]: freeMargin,
        [`accounts.${accountType}.accountSummary.equity.usedEquity`]: balanceData.usedMargin,
        [`accounts.${accountType}.accountSummary.pnl.realizedPnL`]: balanceData.realizedPnL,
        [`accounts.${accountType}.accountSummary.pnl.unrealizedPnL`]: unrealizedPnL,
        [`accounts.${accountType}.accountSummary.pnl.totalPnL`]: balanceData.realizedPnL + unrealizedPnL,
        [`accounts.${accountType}.lastUpdated`]: serverTimestamp()
      });

      
      // Trigger UI updates
      this.triggerBalanceUpdate();
    } catch (error) {
      console.error(`❌ Error updating balance in Firestore for ${accountType} account:`, error);
      throw error;
    }
  }

  /**
   * Log transaction to Firestore
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @param transaction - Transaction data
   */
  private static async logTransaction(
    userId: string,
    accountType: 'demo' | 'live',
    transaction: Omit<BalanceTransaction, 'id'>
  ): Promise<void> {
    try {
      const userDocRef = doc(db, this.COLLECTION_NAME, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }

      const transactionId = `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const transactionData: BalanceTransaction = {
        id: transactionId,
        ...transaction
      };

      const userData = userDoc.data();
      const mappedAccountType = this.mapAccountType(accountType);
      const existingTransactions = userData.accounts?.[mappedAccountType]?.balanceTransactions || [];
      const updatedTransactions = [...existingTransactions, transactionData];

      await updateDoc(userDocRef, {
        [`accounts.${mappedAccountType}.balanceTransactions`]: updatedTransactions
      });

    } catch (error) {
      console.error(`❌ Error logging transaction for ${accountType} account:`, error);
      throw error;
    }
  }

  /**
   * Get transaction history
   * @param userId - User ID
   * @param accountType - Account type ('demo' or 'real')
   * @returns Array of transactions
   */
  static async getTransactionHistory(
    userId: string,
    accountType: 'demo' | 'live'
  ): Promise<BalanceTransaction[]> {
    try {
      const userDocRef = doc(db, this.COLLECTION_NAME, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return [];
      }

      const userData = userDoc.data();
      const transactions = userData.accounts?.[this.mapAccountType(accountType)]?.balanceTransactions || [];
      
      return transactions.sort((a: BalanceTransaction, b: BalanceTransaction) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
    } catch (error) {
      console.error(`❌ Error getting transaction history for ${accountType} account:`, error);
      return [];
    }
  }

  /**
   * Validate balance consistency
   * @param balance - Balance to validate
   * @returns Validation result
   */
  static validateBalanceConsistency(balance: SimplifiedBalanceData): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check if available balance calculation is correct
    const expectedAvailableBalance = balance.totalBalance - balance.usedMargin;
    if (Math.abs(balance.availableBalance - expectedAvailableBalance) > 0.01) {
      errors.push(`Available balance mismatch. Expected: ${expectedAvailableBalance}, Actual: ${balance.availableBalance}`);
    }

    // Check for negative balances
    if (balance.totalBalance < 0) {
      errors.push('Total balance cannot be negative');
    }

    if (balance.availableBalance < 0) {
      warnings.push('Available balance is negative - insufficient margin');
    }

    if (balance.usedMargin < 0) {
      errors.push('Used margin cannot be negative');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
}
